import { QRCodeSVG } from "qrcode.react";
import { useRef } from "react";

export default function QRPage() {
  const qrValue = "https://www.jastiphysiochiro.com/token";
  const qrRef = useRef(null);

  // ===== High-res QR (WITHOUT logo, we add logo manually)
  const getHighResQRCanvas = async (value, size = 800) => {
    const QRCode = (await import("qrcode")).default;
    const canvas = document.createElement("canvas");

    await QRCode.toCanvas(canvas, value, {
      width: size,
      margin: 0,
      errorCorrectionLevel: "H",
    });

    return canvas;
  };

  // ===== Download Card
  const downloadQRCard = async () => {
    const SCALE = 3; // 🔥 DPI SCALE
    const CARD_WIDTH = 360;
    const CARD_HEIGHT = 480;
    const RADIUS = 26;

    const qrCanvas = await getHighResQRCanvas(qrValue, 800);

    const canvas = document.createElement("canvas");
    canvas.width = CARD_WIDTH * SCALE;
    canvas.height = CARD_HEIGHT * SCALE;
    canvas.style.width = `${CARD_WIDTH}px`;
    canvas.style.height = `${CARD_HEIGHT}px`;

    const ctx = canvas.getContext("2d");
    ctx.scale(SCALE, SCALE);
    ctx.imageSmoothingEnabled = false;

    const font =
      "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto";

    const drawRoundedRect = (x, y, w, h, r) => {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + r);
      ctx.lineTo(x + w, y + h - r);
      ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      ctx.lineTo(x + r, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
    };

    /* ===== Card ===== */
    drawRoundedRect(0, 0, CARD_WIDTH, CARD_HEIGHT, RADIUS);
    ctx.fillStyle = "#1f3f87";
    ctx.fill();

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    /* ===== Title ===== */
    ctx.fillStyle = "#ffffff";
    ctx.font = `700 20px ${font}`;
    ctx.fillText("Visakha Physiotherapy", CARD_WIDTH / 2, 48);
    ctx.fillText("& Jasti Chiropractic", CARD_WIDTH / 2, 74);

    /* ===== Tagline ===== */
    ctx.font = `13px ${font}`;
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.fillText("Restoring Health, Reviving Life", CARD_WIDTH / 2, 102);

    /* ===== White QR Card ===== */
    const BOX_X = 18;
    const BOX_Y = 132;
    const BOX_W = CARD_WIDTH - 36;
    const BOX_H = 280;

    ctx.shadowColor = "rgba(0,0,0,0.18)";
    ctx.shadowBlur = 18;
    ctx.shadowOffsetY = 6;

    drawRoundedRect(BOX_X, BOX_Y, BOX_W, BOX_H, 18);
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.shadowColor = "transparent";

    /* ===== QR Label ===== */
    ctx.fillStyle = "#1f3f87";
    ctx.font = `600 13px ${font}`;
    ctx.fillText("Scan to Get Your Token", CARD_WIDTH / 2, BOX_Y + 26);

    /* ===== QR ===== */
    const QR_SIZE = 210;
    const QR_X = CARD_WIDTH / 2 - QR_SIZE / 2;
    const QR_Y = BOX_Y + 50;

    ctx.drawImage(qrCanvas, QR_X, QR_Y, QR_SIZE, QR_SIZE);

    /* ===== LOGO (CENTER OF QR) ===== */
    const logo = new Image();
    logo.src = "/VPJCLogo-New.png";

    await new Promise((res) => {
      logo.onload = res;
    });

    const LOGO_SIZE = 48;
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.arc(
      CARD_WIDTH / 2,
      QR_Y + QR_SIZE / 2,
      LOGO_SIZE / 1.6,
      0,
      Math.PI * 2
    );
    ctx.fill();

    ctx.drawImage(
      logo,
      CARD_WIDTH / 2 - LOGO_SIZE / 2,
      QR_Y + QR_SIZE / 2 - LOGO_SIZE / 2,
      LOGO_SIZE,
      LOGO_SIZE
    );

    /* ===== Footer ===== */
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.font = `12px ${font}`;
    ctx.fillText(
      "www.jastiphysiochiro.com",
      CARD_WIDTH / 2,
      CARD_HEIGHT - 32
    );

    /* ===== Download ===== */
    const link = document.createElement("a");
    link.download = "VPJC-QR.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
       <header className="bg-white w-full">
        <div className="w-full max-w-sm mx-auto px-4 py-4 flex items-center gap-3">
          <img
            src="/VPJCLogo-New.png"
            alt="VPJC Logo"
            className="w-9 h-9 object-contain flex-shrink-0"
          />

          <div className="leading-tight">
            <h1 className="text-sm font-bold text-[#204087]">
              Visakha Physiotherapy
              <br />& Jasti Chiropractic
            </h1>
            <p className="text-[11px] text-gray-500">
              Restoring Health, Reviving Life
            </p>
          </div>
        </div>

        <div className="h-px bg-gray-200" />
      </header>
      <main className="flex-1 bg-slate-100 px-4 py-6 sm:py-10">
        <div
          ref={qrRef}
          className="bg-[#204087] text-white rounded-2xl p-6 text-center shadow-xl"
        >
          <h1 className="text-xl font-bold">
            Visakha Physiotherapy
            <br />& Jasti Chiropractic
          </h1>

          <p className="text-xs opacity-90 mt-1">
            Restoring Health, Reviving Life
          </p>

          <div className="bg-white rounded-xl p-4 my-5">
            <p className="text-xs font-semibold text-[#204087] mb-3">
              Scan to Get Your Token
            </p>

            <div className="relative w-[210px] h-[210px] mx-auto">
  {/* QR ONLY (no logo inside SVG) */}
  <QRCodeSVG
    value={qrValue}
    level="H"
    className="w-full h-full"
    includeMargin={false}
  />

  {/* White circular background */}
  <div
    className="absolute left-1/2 top-1/2
               w-[56px] h-[56px]
               -translate-x-1/2 -translate-y-1/2
               rounded-full bg-white
               z-10"
  />

  {/* Logo on top */}
  <img
    src="/VPJCLogo-New.png"
    alt="Logo"
    className="absolute left-1/2 top-1/2
               w-[42px] h-[42px]
               -translate-x-1/2 -translate-y-1/2
               rounded-full
               object-contain
               z-20"
  />
</div>

          </div>

          <p className="text-xs opacity-90">www.jastiphysiochiro.com</p>

          <button
            onClick={downloadQRCard}
            className="mt-4 w-full bg-white text-[#204087] rounded-full py-2 font-semibold"
          >
            Download QR
          </button>
        </div>
      </main>
      {/* ===== FOOTER ===== */}
      <footer className="mt-auto bg-white w-full">
        <div className="w-full max-w-sm mx-auto py-4 text-center text-[11px] sm:text-xs px-4 text-gray-500">
          © {new Date().getFullYear()} VPJC ·{" "}
          <a
            href="https://www.charvionglobal.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-[#204087] hover:underline transition"
          >
            Powered by CGS
          </a>
        </div>
      </footer>

    </div>
  );
}
